import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ControllerValidatedTextField,
  Typography,
  useForm,
  yupResolver,
  Stack,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  IoClose,
} from '@bymason-private/legacy-pattern-components';
import { RequestDeviceFormFields, useSubmitRequestDeviceForm } from '@bymason-private/controller-api-hooks';
import { pxToRem, pxToLineHeight } from '@bymason/utils';

import { Fields, FormData, schema } from './types';
import { useAuth } from '@bymason-private/auth-provider';
import { useGetAccount } from '@bymason/controller-api-hooks';

interface RequestDeviceFormViewProps {
  open: boolean;
  title?: string;
  heading?: ReactElement;
  deviceType?: string;
  groupName?: string;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (message: string, code?: number) => void;
}

export function RequestDeviceModal({
  open,
  title,
  heading,
  deviceType,
  groupName,
  onClose,
  onSuccess,
  onError,
}: RequestDeviceFormViewProps) {
  const { userInfo, authToken = '' } = useAuth();
  const [getAccount, accountData] = useGetAccount(authToken);

  const fieldDefaults = useMemo(
    () => ({
      email: userInfo?.email || '',
      firstName: userInfo?.given_name || '',
      lastName: userInfo?.family_name || '',
      companyName: accountData?.companyName || '',
      jobTitle: '',
    }),
    [userInfo, accountData]
  );

  const [requestDevice, , requestDeviceError, requestDeviceBusy] = useSubmitRequestDeviceForm(authToken);
  const [pendingRequest, setPendingRequest] = useState(false);

  const {
    control,
    formState: { isValid },
    getValues,
    setValue,
    reset,
    trigger,
  } = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      [Fields.FirstName]: fieldDefaults?.firstName,
      [Fields.LastName]: fieldDefaults?.lastName,
      [Fields.Company]: fieldDefaults?.companyName,
      [Fields.Email]: fieldDefaults?.email,
      [Fields.Title]: '',
    },
    resolver: yupResolver(schema),
  });

  const handleSubmit = useCallback(() => {
    trigger();

    if (isValid) {
      const { firstname, lastname, companyName, email, jobTitle } = getValues();

      const data: RequestDeviceFormFields = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        companyName: companyName.trim(),
        jobTitle: jobTitle.trim(),
        email: email.trim(),
        deviceType: deviceType || '',
        groupName: groupName || '',
      };

      setPendingRequest(true);
      requestDevice(data);
    }
  }, [trigger, isValid, getValues, deviceType, groupName, requestDevice]);

  useEffect(() => {
    if (pendingRequest && !requestDeviceBusy && !requestDeviceError) {
      setPendingRequest(false);
      onSuccess && onSuccess();
      onClose();
    }
  }, [onClose, onSuccess, pendingRequest, requestDeviceBusy, requestDeviceError]);

  useEffect(() => {
    if (requestDeviceError && !requestDeviceBusy) {
      setPendingRequest(false);
      onError && onError(requestDeviceError);
    }
  }, [onError, requestDeviceBusy, requestDeviceError]);

  useEffect(() => {
    if (open) {
      reset();
      trigger();
    }
  }, [open, reset, trigger]);

  useEffect(() => {
    getAccount({});
  }, [getAccount]);

  useEffect(() => {
    if (accountData) {
      setValue('companyName', accountData.companyName);
    }
  }, [accountData, setValue]);

  return (
    <Dialog onClose={onClose} open={open} PaperProps={{ sx: { p: 3 } }}>
      <Box sx={{ position: 'relative' }}>
        <IconButton color='inherit' onClick={onClose} sx={{ position: 'absolute', right: -8, top: -8 }}>
          <IoClose />
        </IconButton>
      </Box>
      <DialogTitle sx={{ fontSize: pxToRem(24), fontWeight: 600, lineHeight: pxToLineHeight(24, 40), pt: 4 }}>
        {title || 'See your apps in action'}
      </DialogTitle>
      <DialogContent>
        <Stack direction='column' spacing={4}>
          <Typography sx={{ fontSize: pxToRem(18), lineHeight: pxToLineHeight(18, 26) }}>
            {heading ||
              'Let our customer success team help you get a Mason development kit provisioned so you can see your deployed apps in action!'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              gap: 2,
            }}
          >
            <ControllerValidatedTextField
              label='First Name'
              name={Fields.FirstName}
              id={'given-name'}
              autoComplete='given-name'
              required
              fullWidth
              control={control}
            />
            <ControllerValidatedTextField
              label='Last Name'
              name={Fields.LastName}
              id={'family-name'}
              autoComplete='family-name'
              required
              fullWidth
              control={control}
            />
          </Box>
          <ControllerValidatedTextField
            label='Email'
            name={Fields.Email}
            id='email'
            autoComplete='email'
            type='email'
            required
            fullWidth
            control={control}
          />
          <ControllerValidatedTextField
            autoFocus
            label='Job Title'
            name={Fields.Title}
            id='job-title'
            autoComplete='job-title'
            required
            fullWidth
            control={control}
          />
          <ControllerValidatedTextField
            label='Company Name'
            name={Fields.Company}
            id='organization'
            autoComplete='organization'
            required
            fullWidth
            control={control}
          />

          <Box>
            <Button
              fullWidth
              disabled={requestDeviceBusy || !isValid}
              onClick={handleSubmit}
              variant='contained'
            >
              Submit to Customer Success
            </Button>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
